import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Tabs from '../../../components/tabs';
import SitePages from './components/SitePages';
import SiteInformation from './components/SiteInformation';
import SiteSeoCheck from './components/SiteSeoCheck';
import SiteAccess from './components/SiteAccess';
import SiteLinks from './components/SiteLinks';
import SiteTasks from './components/SiteTasks';

const SiteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchWithAuth, currentLang, API_URL, user } = useAuth();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('important-pages');

  // Проверяем, является ли текущий пользователь владельцем сайта
  const isOwner = user?.id === site?.ucr;

  const tabs = [
    {
      id: 'important-pages',
      label: currentLang === 'ru' ? 'Важные страницы сайта' : 'Important Pages'
    },
    {
      id: 'information',
      label: currentLang === 'ru' ? 'Информация' : 'Information'
    },
    {
      id: 'seo-check',
      label: currentLang === 'ru' ? 'SEO Проверка' : 'SEO Check'
    },
    ...(user?.role === 'admin' ? [
    {
      id: 'links',
      label: currentLang === 'ru' ? 'Ссылки' : 'Links'
    },
    {
      id: 'tasks',
      label: currentLang === 'ru' ? 'Задачи' : 'Tasks'
    },
    ] : []),
    // Таб управления доступом только для владельца
    ...(isOwner ? [{
      id: 'access',
      label: currentLang === 'ru' ? 'Управление доступом' : 'Access Management'
    }] : [])
  ];

  useEffect(() => {
    fetchSite();
  }, [id, currentLang]);

  const fetchSite = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${id}`);

      const data = await response.json();

      if (response.ok && data.success) {
        setSite(data.site);
      } else {
        setError(data.message || 'Ошибка загрузки сайта');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError('Ошибка сети при загрузке сайта');
      }
      console.error('Ошибка загрузки сайта:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return currentLang === 'ru' ? 'Не указана' : 'Not specified';
    return new Date(dateString).toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return currentLang === 'ru' ? 'Не указана' : 'Not specified';
    return new Date(timestamp * 1000).toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US');
  };

  const handleSiteUpdate = (updatedSite) => {
    console.log('SiteDetail: handleSiteUpdate called with:', updatedSite);
    if (updatedSite === null) {
      // Если получили null, перезагружаем данные сайта
      console.log('SiteDetail: Reloading site data after update');
      fetchSite();
    } else if (updatedSite && typeof updatedSite === 'object') {
      setSite(updatedSite);
    } else {
      console.error('SiteDetail: Invalid updatedSite data:', updatedSite);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {error}
        </div>
        <button
          onClick={() => navigate('/seo/sites')}
          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
        >
          {currentLang === 'ru' ? 'Вернуться к списку' : 'Back to List'}
        </button>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center py-8 text-gray-500">
          {currentLang === 'ru' ? 'Сайт не найден' : 'Site not found'}
        </div>
        <button
          onClick={() => navigate('/seo/sites')}
          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
        >
          {currentLang === 'ru' ? 'Вернуться к списку' : 'Back to List'}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 sm:gap-0">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold flex items-center flex-wrap gap-2">
            <button
              onClick={() => navigate('/seo/sites')}
              className="px-2 sm:px-3 py-1 bg-gray-200 text-black rounded-lg hover:bg-gray-600 text-sm sm:text-base"
            >
              ←
            </button>
            <span className="break-all">{site.dns}</span>
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold rounded-full ${
            site.status === 1 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {site.status === 1 ? (currentLang === 'ru' ? 'Активен' : 'Active') : (currentLang === 'ru' ? 'Неактивен' : 'Inactive')}
          </span>
          <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold rounded-full ${
            site.https === 1 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {site.https === 1 ? 'HTTPS' : 'HTTP'}
          </span>
          <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold rounded-full ${
            site.tarif === 1 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {site.tarif === 1 ? (currentLang === 'ru' ? 'Премиум' : 'Premium') : (currentLang === 'ru' ? 'Базовый' : 'Basic')}
          </span>
        </div>
      </div>

      {/* Табы */}
      <div className="overflow-x-auto">
        <Tabs 
          tabs={tabs} 
          activeTab={activeTab} 
          onTabChange={setActiveTab} 
        />
      </div>

      {/* Контент табов */}
      <div className="mt-4 sm:mt-6">
        {activeTab === 'important-pages' && (
          <SitePages site={site} />
        )}
        {activeTab === 'information' && (
          <SiteInformation 
            site={site} 
            formatDate={formatDate} 
            formatTimestamp={formatTimestamp}
            onSiteUpdate={handleSiteUpdate}
          />
        )}
        {activeTab === 'seo-check' && (
          <SiteSeoCheck site={site} />
        )}
        {activeTab === 'links' && (
          <SiteLinks site={site} />
        )}
        {activeTab === 'tasks' && (
          <SiteTasks site={site} />
        )}
        {activeTab === 'access' && (
          <SiteAccess site={site} />
        )}
      </div>
    </div>
  );
};

export default SiteDetail; 